<app-page-shell>
    <p-toast position="top-center"></p-toast>

    <app-page-hero eyebrow="Finance Admin" title="Manual Entry Controls"
        subtitle="Maintain approval controls, tax codes, and payment terms without leaving the control library.">
        <div actions>
            <p-button [label]="activeTabAddLabel" icon="pi pi-plus" (click)="openActiveDialog()"></p-button>
        </div>
    </app-page-hero>

    <app-page-data-panel title="Control library"
        subtitle="Choose a control type to review its current setup, then add or edit it in a focused dialog.">
        <p-tabs [value]="activeTab" (valueChange)="onTabChange($event)" styleClass="manual-entry-control-tabs">
            <p-tablist>
                <p-tab value="reason-codes">
                    <span class="tab-label"><i class="pi pi-tags"></i> Reason Codes <span class="tab-count">{{ reasonCodes.length }}</span></span>
                </p-tab>
                <p-tab value="policies">
                    <span class="tab-label"><i class="pi pi-shield"></i> Entry Policies <span class="tab-count">{{ policies.length }}</span></span>
                </p-tab>
                <p-tab value="tax-codes">
                    <span class="tab-label"><i class="pi pi-percentage"></i> Tax Codes <span class="tab-count">{{ taxCodes.length }}</span></span>
                </p-tab>
                <p-tab value="payment-terms">
                    <span class="tab-label"><i class="pi pi-credit-card"></i> Payment Terms <span class="tab-count">{{ paymentTerms.length }}</span></span>
                </p-tab>
            </p-tablist>

            <p-tabpanels>
                <p-tabpanel value="reason-codes">
                    <div class="list-toolbar">
                        <div>
                            <h3>Reason Codes</h3>
                            <p>Reason codes drive approval requirements. Supporting documents remain optional.</p>
                        </div>
                        <p-iftaLabel>
                            <p-select inputId="reasonListDocumentType" [options]="documentTypes"
                                [(ngModel)]="reasonListDocumentType" (onChange)="loadReasonCodes()" styleClass="reason-document-select"></p-select>
                            <label for="reasonListDocumentType">Document type</label>
                        </p-iftaLabel>
                    </div>
                    <p-table [value]="reasonCodes" responsiveLayout="stack" styleClass="p-datatable-sm">
                        <ng-template pTemplate="header">
                            <tr><th>Document</th><th>Code</th><th>Description</th><th>Approval</th><th class="actions-column">Actions</th></tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-code>
                            <tr>
                                <td><span class="document-type">{{ code.documentType }}</span></td>
                                <td><strong>{{ code.reasonCode }}</strong></td>
                                <td>{{ code.description }}</td>
                                <td><span [class]="code.requiresApproval ? 'status-required' : 'status-optional'">{{ code.requiresApproval ? 'Required' : 'Not required' }}</span></td>
                                <td class="actions-column"><p-button label="Edit" icon="pi pi-pencil" size="small" severity="secondary" [outlined]="true" (click)="editReasonCode(code)"></p-button></td>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage"><tr><td colspan="5" class="empty-state">No reason codes for this document type.</td></tr></ng-template>
                    </p-table>
                </p-tabpanel>

                <p-tabpanel value="policies">
                    <div class="list-toolbar">
                        <div>
                            <h3>Manual Entry Policies</h3>
                            <p>Policies determine approval requirements by document type and source.</p>
                        </div>
                    </div>
                    <p-table [value]="policies" responsiveLayout="stack" styleClass="p-datatable-sm">
                        <ng-template pTemplate="header">
                            <tr><th>Document</th><th>Source</th><th>Role</th><th>Threshold</th><th>Approval</th><th class="actions-column">Actions</th></tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-policy>
                            <tr>
                                <td><span class="document-type">{{ policy.documentType }}</span></td>
                                <td>{{ policy.sourceType }}</td>
                                <td>{{ policy.roleName || '-' }}</td>
                                <td>{{ policy.amountThreshold == null ? '-' : policy.amountThreshold }}</td>
                                <td><span [class]="policy.approvalRequired ? 'status-required' : 'status-optional'">{{ policy.approvalRequired ? 'Required' : 'Not required' }}</span></td>
                                <td class="actions-column"><p-button label="Edit" icon="pi pi-pencil" size="small" severity="secondary" [outlined]="true" (click)="editPolicy(policy)"></p-button></td>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage"><tr><td colspan="6" class="empty-state">No manual-entry policies configured.</td></tr></ng-template>
                    </p-table>
                </p-tabpanel>

                <p-tabpanel value="tax-codes">
                    <div class="list-toolbar">
                        <div>
                            <h3>Tax Codes</h3>
                            <p>Tax codes are used by manual receivable, payable, invoice, and bill lines.</p>
                        </div>
                    </div>
                    <p-table [value]="taxCodes" responsiveLayout="stack" styleClass="p-datatable-sm">
                        <ng-template pTemplate="header">
                            <tr><th>Code</th><th>Description</th><th>Rate</th><th>Category</th><th class="actions-column">Actions</th></tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-code>
                            <tr>
                                <td><strong>{{ code.taxCode }}</strong></td>
                                <td>{{ code.description || '-' }}</td>
                                <td>{{ code.taxRate ?? 0 }}%</td>
                                <td>{{ code.taxCategory || '-' }}</td>
                                <td class="actions-column"><p-button label="Edit" icon="pi pi-pencil" size="small" severity="secondary" [outlined]="true" (click)="editTaxCode(code)"></p-button></td>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage"><tr><td colspan="5" class="empty-state">No tax codes configured.</td></tr></ng-template>
                    </p-table>
                </p-tabpanel>

                <p-tabpanel value="payment-terms">
                    <div class="list-toolbar">
                        <div>
                            <h3>Tenant Payment Terms</h3>
                            <p>Manage reusable payment schedules for sales, shipments, and accounting documents.</p>
                        </div>
                    </div>
                    <p-table [value]="paymentTerms" responsiveLayout="stack" styleClass="p-datatable-sm">
                        <ng-template pTemplate="header">
                            <tr><th>Name</th><th>Description</th><th>Days</th><th>Updated</th><th class="actions-column">Actions</th></tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-term>
                            <tr>
                                <td><strong>{{ term.name }}</strong></td>
                                <td>{{ term.description || '-' }}</td>
                                <td>{{ term.days ?? 0 }}</td>
                                <td>{{ term.updateDate | date:'short' }}</td>
                                <td class="actions-column">
                                    <div class="row-actions">
                                        <p-button label="Edit" icon="pi pi-pencil" size="small" severity="secondary" [outlined]="true" (click)="editPaymentTerm(term)"></p-button>
                                        <p-button *ngIf="term.paymentTermID" label="Archive" icon="pi pi-box" size="small" severity="warn" [outlined]="true" (click)="archivePaymentTerm(term)"></p-button>
                                    </div>
                                </td>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage"><tr><td colspan="5" class="empty-state">No payment terms configured.</td></tr></ng-template>
                    </p-table>
                </p-tabpanel>
            </p-tabpanels>
        </p-tabs>
    </app-page-data-panel>

    <app-page-data-panel title="Finance note posting accounts"
        subtitle="Choose the control accounts for finance notes. A claim suspense account is required for vendor debit claims.">
        <div class="note-account-grid">
            <p-inputGroup><p-inputGroupAddon><i class="pi pi-book"></i></p-inputGroupAddon><p-iftalabel>
                <p-select inputId="noteReceivable" [options]="noteAccounts" optionLabel="accountName" optionValue="accountId" [filter]="true" [showClear]="true" appendTo="body" styleClass="w-full" [(ngModel)]="noteSettings.receivableAccountId"></p-select><label for="noteReceivable">Receivables</label>
            </p-iftalabel></p-inputGroup>
            <p-inputGroup><p-inputGroupAddon><i class="pi pi-book"></i></p-inputGroupAddon><p-iftalabel>
                <p-select inputId="notePayable" [options]="noteAccounts" optionLabel="accountName" optionValue="accountId" [filter]="true" [showClear]="true" appendTo="body" styleClass="w-full" [(ngModel)]="noteSettings.payableAccountId"></p-select><label for="notePayable">Payables</label>
            </p-iftalabel></p-inputGroup>
            <p-inputGroup><p-inputGroupAddon><i class="pi pi-percentage"></i></p-inputGroupAddon><p-iftalabel>
                <p-select inputId="noteOutputVat" [options]="noteAccounts" optionLabel="accountName" optionValue="accountId" [filter]="true" [showClear]="true" appendTo="body" styleClass="w-full" [(ngModel)]="noteSettings.outputVatAccountId"></p-select><label for="noteOutputVat">Output VAT</label>
            </p-iftalabel></p-inputGroup>
            <p-inputGroup><p-inputGroupAddon><i class="pi pi-percentage"></i></p-inputGroupAddon><p-iftalabel>
                <p-select inputId="noteInputVat" [options]="noteAccounts" optionLabel="accountName" optionValue="accountId" [filter]="true" [showClear]="true" appendTo="body" styleClass="w-full" [(ngModel)]="noteSettings.inputVatAccountId"></p-select><label for="noteInputVat">Input VAT</label>
            </p-iftalabel></p-inputGroup>
            <p-inputGroup><p-inputGroupAddon><i class="pi pi-shield"></i></p-inputGroupAddon><p-iftalabel>
                <p-select inputId="noteClaimSuspense" [options]="noteAccounts" optionLabel="accountName" optionValue="accountId" [filter]="true" [showClear]="true" appendTo="body" styleClass="w-full" [(ngModel)]="noteSettings.claimSuspenseAccountId"></p-select><label for="noteClaimSuspense">Claim suspense</label>
            </p-iftalabel></p-inputGroup>
        </div>
        <p>When a control account is left blank, the standard named account is used if available.</p>
        <p-button label="Save note accounts" icon="pi pi-check" [loading]="savingNoteSettings" (click)="saveNoteSettings()"></p-button>
    </app-page-data-panel>

    <p-dialog [(visible)]="dialogVisible" (onHide)="closeDialog()" [modal]="true" [header]="dialogTitle"
        [draggable]="false" [resizable]="false" [style]="{ width: 'min(900px, 94vw)' }"
        [breakpoints]="{ '760px': '96vw' }" styleClass="manual-entry-create-dialog">
        <section class="control-dialog-body">
            <aside class="control-dialog-summary">
                <div class="summary-icon"><i [class]="dialogIcon"></i></div>
                <div>
                    <small>{{ dialogEyebrow }}</small>
                    <h3>{{ dialogSummaryTitle }}</h3>
                    <p>{{ dialogSummaryText }}</p>
                </div>
                <div class="control-dialog-facts">
                    <div><span>{{ dialogPrimaryFactLabel }}</span><strong>{{ dialogPrimaryFactValue }}</strong></div>
                    <div><span>{{ dialogSecondaryFactLabel }}</span><strong>{{ dialogSecondaryFactValue }}</strong></div>
                </div>
            </aside>

            <section class="control-dialog-form">
                @if (activeDialog === 'reason') {
                    <p-inputGroup class="dialog-wide-field">
                        <p-inputGroupAddon><i class="pi pi-file"></i></p-inputGroupAddon>
                        <p-iftaLabel>
                            <p-select inputId="reasonDocumentType" [options]="documentTypes" [(ngModel)]="reasonDraft.documentType" appendTo="body" styleClass="w-full"></p-select>
                            <label for="reasonDocumentType">Document type *</label>
                        </p-iftaLabel>
                    </p-inputGroup>
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon>
                        <p-iftaLabel><input id="reasonCode" pInputText [(ngModel)]="reasonDraft.reasonCode" /><label for="reasonCode">Reason code *</label></p-iftaLabel>
                    </p-inputGroup>
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-align-left"></i></p-inputGroupAddon>
                        <p-iftaLabel><input id="reasonDescription" pInputText [(ngModel)]="reasonDraft.description" /><label for="reasonDescription">Description *</label></p-iftaLabel>
                    </p-inputGroup>
                    <label class="dialog-checkbox dialog-wide-field"><p-checkbox [(ngModel)]="reasonDraft.requiresApproval" [binary]="true"></p-checkbox> Requires approval</label>
                }

                @if (activeDialog === 'policy') {
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-file"></i></p-inputGroupAddon>
                        <p-iftaLabel>
                            <p-select inputId="policyDocumentType" [options]="documentTypes" [(ngModel)]="policyDraft.documentType" appendTo="body" styleClass="w-full"></p-select>
                            <label for="policyDocumentType">Document type *</label>
                        </p-iftaLabel>
                    </p-inputGroup>
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-sitemap"></i></p-inputGroupAddon>
                        <p-iftaLabel>
                            <p-select inputId="policySourceType" [options]="sourceTypes" [(ngModel)]="policyDraft.sourceType" appendTo="body" styleClass="w-full"></p-select>
                            <label for="policySourceType">Source type *</label>
                        </p-iftaLabel>
                    </p-inputGroup>
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-users"></i></p-inputGroupAddon>
                        <p-iftaLabel><input id="policyRole" pInputText [(ngModel)]="policyDraft.roleName" /><label for="policyRole">Role</label></p-iftaLabel>
                    </p-inputGroup>
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-money-bill"></i></p-inputGroupAddon>
                        <p-iftaLabel>
                            <p-inputNumber inputId="policyAmountThreshold" [(ngModel)]="policyDraft.amountThreshold" mode="decimal" [min]="0" styleClass="w-full"></p-inputNumber>
                            <label for="policyAmountThreshold">Amount threshold</label>
                        </p-iftaLabel>
                    </p-inputGroup>
                    <label class="dialog-checkbox dialog-wide-field"><p-checkbox [(ngModel)]="policyDraft.approvalRequired" [binary]="true"></p-checkbox> Approval required</label>
                }

                @if (activeDialog === 'tax-code') {
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon>
                        <p-iftaLabel><input id="taxCode" pInputText [(ngModel)]="taxCodeDraft.taxCode" /><label for="taxCode">Tax code *</label></p-iftaLabel>
                    </p-inputGroup>
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-align-left"></i></p-inputGroupAddon>
                        <p-iftaLabel><input id="taxDescription" pInputText [(ngModel)]="taxCodeDraft.description" /><label for="taxDescription">Description</label></p-iftaLabel>
                    </p-inputGroup>
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-percentage"></i></p-inputGroupAddon>
                        <p-iftaLabel>
                            <p-inputNumber inputId="taxRate" [(ngModel)]="taxCodeDraft.taxRate" mode="decimal" [min]="0" styleClass="w-full"></p-inputNumber>
                            <label for="taxRate">Tax rate %</label>
                        </p-iftaLabel>
                    </p-inputGroup>
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-tag"></i></p-inputGroupAddon>
                        <p-iftaLabel><input id="taxCategory" pInputText [(ngModel)]="taxCodeDraft.taxCategory" /><label for="taxCategory">Category</label></p-iftaLabel>
                    </p-inputGroup>
                }

                @if (activeDialog === 'payment-term') {
                    <p-inputGroup class="dialog-wide-field">
                        <p-inputGroupAddon><i class="pi pi-credit-card"></i></p-inputGroupAddon>
                        <p-iftaLabel><input id="paymentTermName" pInputText [(ngModel)]="paymentTermDraft.name" /><label for="paymentTermName">Name *</label></p-iftaLabel>
                    </p-inputGroup>
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon>
                        <p-iftaLabel>
                            <p-inputNumber inputId="paymentTermDays" [(ngModel)]="paymentTermDraft.days" [min]="0" styleClass="w-full"></p-inputNumber>
                            <label for="paymentTermDays">Days *</label>
                        </p-iftaLabel>
                    </p-inputGroup>
                    <p-inputGroup class="dialog-wide-field">
                        <p-inputGroupAddon><i class="pi pi-align-left"></i></p-inputGroupAddon>
                        <p-iftaLabel><input id="paymentTermDescription" pInputText [(ngModel)]="paymentTermDraft.description" /><label for="paymentTermDescription">Description *</label></p-iftaLabel>
                    </p-inputGroup>
                }
            </section>
        </section>

        <ng-template pTemplate="footer">
            <div class="control-dialog-footer">
                <p-button type="button" label="Reset" icon="pi pi-refresh" severity="secondary" [outlined]="true" (click)="resetActiveDraft()"></p-button>
                <span class="footer-spacer"></span>
                <p-button type="button" label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (click)="closeDialog()"></p-button>
                <p-button type="button" [label]="dialogSubmitLabel" icon="pi pi-save" [disabled]="!canSaveActiveDraft()" (click)="saveActiveDraft()"></p-button>
            </div>
        </ng-template>
    </p-dialog>
</app-page-shell>
